import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Download,
  FileText,
  Lock,
  Sparkles,
  Trash2,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { SelectNative } from "../components/ui/select-native";
import {
  analyzePolicyPDF,
  analysisPdfUrl,
  deleteAnalysis,
  fetchAnalyses,
  fetchAnalysis,
  fetchAnalyzerCriteria,
  fetchPolicies,
  reanalyzePolicy,
} from "../lib/api";
import { normalizeDetail } from "../lib/errors";

const VERDICT_STYLES = {
  excellent: { label: "Excellent", tone: "text-emerald-300", ring: "border-emerald-400/60", chip: "bg-emerald-400/15 text-emerald-300" },
  good: { label: "Good", tone: "text-gold", ring: "border-gold/60", chip: "bg-gold/15 text-gold" },
  average: { label: "Average", tone: "text-amber-300", ring: "border-amber-400/60", chip: "bg-amber-400/15 text-amber-300" },
  poor: { label: "Poor", tone: "text-red-400", ring: "border-red-500/60", chip: "bg-red-500/15 text-red-400" },
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 180_000; // 3 minutes — matches backend Gemini budget

async function pollUntilComplete(analysisId) {
  const started = Date.now();
  // Give Gemini ~3 minutes; anything longer we surface as a "still processing" hint.
  while (Date.now() - started < POLL_TIMEOUT_MS) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    const doc = await fetchAnalysis(analysisId);
    if (doc.status && doc.status !== "processing") return doc;
  }
  return { status: "failed", error_message: "The AI is taking longer than expected. Refresh in a minute to see the result — it will keep processing in the background." };
}

/**
 * Health Policy AI Analyzer (Phase 3) — upload a policy PDF, get a 0-100 scorecard against
 * the 11 must-have criteria. The uploaded PDF is stored in Emergent object storage and can be
 * downloaded again from the analysis card.
 */
export function AnalyzePolicyPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]); // up to 2 File objects
  const [password, setPassword] = useState("");
  const [policyId, setPolicyId] = useState("");
  const [policies, setPolicies] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | analyzing | result | error
  const [progress, setProgress] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [criteria, setCriteria] = useState([]);

  const refreshHistory = useCallback(async () => {
    try {
      setHistory(await fetchAnalyses());
    } catch {
      // silently ignore — the empty state below is fine
    }
  }, []);

  useEffect(() => {
    fetchPolicies()
      .then(setPolicies)
      .catch(() => setPolicies([]));
    fetchAnalyzerCriteria()
      .then((res) => setCriteria(res.criteria || []))
      .catch(() => setCriteria([]));
    refreshHistory();
  }, [refreshHistory]);

  function addFiles(fileList) {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setFiles((prev) => {
      const merged = [...prev];
      for (const f of incoming) {
        if (merged.length >= 2) {
          toast.error("You can upload at most 2 PDFs.");
          break;
        }
        if (!f.name.toLowerCase().endsWith(".pdf")) {
          toast.error(`"${f.name}" is not a PDF.`);
          continue;
        }
        if (merged.some((m) => m.name === f.name && m.size === f.size)) continue;
        merged.push(f);
      }
      return merged;
    });
  }

  function removeFile(idx) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!files.length) {
      toast.error("Please choose at least one PDF.");
      return;
    }
    setStatus("analyzing");
    setProgress(0);
    try {
      const res = await analyzePolicyPDF({
        files,
        password: password || undefined,
        policyId: policyId || undefined,
        onProgress: setProgress,
      });
      // If the backend returned a still-processing doc, poll until Gemini finishes.
      if (res.status === "processing") {
        setAnalysis(res);
        setStatus("scoring");
        const finalDoc = await pollUntilComplete(res.id);
        if (finalDoc.status === "failed") {
          setStatus("error");
          toast.error(finalDoc.error_message || "The AI scoring step failed. Please try again.");
          setAnalysis(finalDoc);
          await refreshHistory();
          return;
        }
        setAnalysis(finalDoc);
      } else {
        setAnalysis(res);
      }
      setStatus("result");
      toast.success("Analysis ready.");
      await refreshHistory();
    } catch (err) {
      setStatus("error");
      const detail = normalizeDetail(err.response?.data?.detail, "Something went wrong.");
      toast.error(detail);
    }
  }

  function reset() {
    setFiles([]);
    setPassword("");
    setPolicyId("");
    setAnalysis(null);
    setProgress(0);
    setStatus("idle");
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this analysis? The stored PDF becomes inaccessible.")) return;
    try {
      await deleteAnalysis(id);
      toast.success("Analysis deleted.");
      if (analysis?.id === id) reset();
      await refreshHistory();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't delete."));
    }
  }

  async function handleReanalyze(id) {
    // Re-run Gemini on the previously-extracted text. Same async pattern as the initial
    // upload — flip local status to "analyzing" and poll for completion.
    try {
      setStatus("analyzing");
      setProgress(100);
      await reanalyzePolicy(id);
      toast.info("Re-analyzing with the latest scoring rubric…");
      const done = await pollUntilComplete(id);
      if (done.status === "completed") {
        setAnalysis(done);
        setStatus("result");
        toast.success("Fresh scorecard ready.");
        await refreshHistory();
      } else {
        setStatus("result");
        toast.error(done.error_message || "Re-analyze failed. Try again in a moment.");
      }
    } catch (err) {
      setStatus("result");
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't start re-analysis."));
    }
  }

  const healthPolicies = useMemo(
    () => (policies || []).filter((p) => p.type === "health" || p.type === "other"),
    [policies],
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <button type="button" onClick={() => navigate(-1)} className="text-xs text-muted hover:text-gold" data-testid="page-back">
        ← Go back
      </button>
      <div className="mt-2 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-serif text-3xl text-text sm:text-4xl">
            Health Policy <span className="text-gold">Analyzer</span>
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            Upload up to two health-insurance PDFs (policy schedule + optional T&amp;C / addendum —
            password-protected fine). We score them together, out of 100, across 4 sections —
            waiting periods, coverage, financial terms, and claim experience — and layer on the
            insurer&apos;s latest IRDAI-published KPIs (CSR, ICR, Solvency, Complaints, Network).
            Nothing leaves your account — the PDFs are stored
            securely and only you can access them.
          </p>
        </div>
        {status === "result" && (
          <Button onClick={reset} variant="outline" className="gap-2">
            <Upload className="h-4 w-4" /> Analyze another
          </Button>
        )}
      </div>

      {status !== "result" && (
        <UploadForm
          files={files}
          addFiles={addFiles}
          removeFile={removeFile}
          password={password}
          setPassword={setPassword}
          policyId={policyId}
          setPolicyId={setPolicyId}
          healthPolicies={healthPolicies}
          onSubmit={onSubmit}
          status={status}
          progress={progress}
        />
      )}

      {status === "result" && analysis && (
        <AnalysisView
          analysis={analysis}
          onDelete={() => handleDelete(analysis.id)}
          onReanalyze={() => handleReanalyze(analysis.id)}
        />
      )}

      {status !== "result" && (
        <CriteriaPreview criteria={criteria} />
      )}

      <HistorySection
        history={history}
        onOpen={(detail) => {
          setAnalysis(detail);
          setStatus("result");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        onDelete={handleDelete}
      />
    </div>
  );
}

/* -------------------------------- Upload form -------------------------------- */

function UploadForm({
  files,
  addFiles,
  removeFile,
  password,
  setPassword,
  policyId,
  setPolicyId,
  healthPolicies,
  onSubmit,
  status,
  progress,
}) {
  const analyzing = status === "analyzing" || status === "scoring";
  const scoring = status === "scoring";
  const canAddMore = files.length < 2;
  return (
    <form onSubmit={onSubmit} className="mt-8" data-testid="analyzer-upload-form">
      <Card className="border-gold/25">
        <CardContent className="flex flex-col gap-5 p-6">
          <div>
            <Label htmlFor="pdf">Policy PDFs * <span className="text-xs text-muted">(1 required, up to 2 total)</span></Label>
            <label
              htmlFor="pdf"
              className={`mt-1.5 flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border bg-elevated/40 px-6 py-10 text-center transition-colors ${
                canAddMore && !analyzing
                  ? "cursor-pointer hover:border-gold/60 hover:bg-elevated/70"
                  : "cursor-not-allowed opacity-60"
              }`}
              data-testid="analyzer-drop-zone"
            >
              <Upload className="h-6 w-6 text-gold" />
              <span className="text-sm text-text">
                {files.length === 0
                  ? "Click to choose PDFs (max 20 MB each)"
                  : canAddMore
                  ? "Add another PDF (e.g. terms & conditions)"
                  : "Maximum of 2 files reached"}
              </span>
              <span className="text-xs text-muted">
                Add the policy schedule and (optionally) the T&amp;C / wording document for a
                richer AI analysis. Same password is applied to both.
              </span>
              <input
                id="pdf"
                type="file"
                accept="application/pdf,.pdf"
                multiple
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = ""; // allow re-selecting the same file after removal
                }}
                disabled={analyzing || !canAddMore}
                className="hidden"
                data-testid="analyzer-file-input"
              />
            </label>

            {files.length > 0 && (
              <ul className="mt-3 flex flex-col gap-2" data-testid="analyzer-file-list">
                {files.map((f, idx) => (
                  <li
                    key={`${f.name}-${f.size}-${idx}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-border bg-elevated/50 px-3 py-2"
                    data-testid={`analyzer-file-chip-${idx}`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <FileText className="h-4 w-4 flex-shrink-0 text-gold" />
                      <div className="min-w-0">
                        <div className="truncate text-sm text-text">{f.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted">
                          {idx === 0 ? "Primary policy" : "Additional context"} · {(f.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      disabled={analyzing}
                      className="flex-shrink-0 rounded-md p-1 text-muted hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
                      data-testid={`analyzer-file-remove-${idx}`}
                      aria-label={`Remove ${f.name}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="pwd" className="flex items-center gap-1.5">
                <Lock className="h-3 w-3 text-gold" /> PDF password{" "}
                <span className="text-xs text-muted">(if protected)</span>
              </Label>
              <Input
                id="pwd"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Same password used for both PDFs"
                disabled={analyzing}
                data-testid="analyzer-password-input"
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="link">Link to existing policy <span className="text-xs text-muted">(optional)</span></Label>
              <SelectNative
                id="link"
                value={policyId}
                onChange={(e) => setPolicyId(e.target.value)}
                disabled={analyzing}
                data-testid="analyzer-policy-select"
              >
                <option value="">— Don&apos;t link —</option>
                {healthPolicies.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.insurer ? `· ${p.insurer}` : ""}
                  </option>
                ))}
              </SelectNative>
            </div>
          </div>

          <Button type="submit" disabled={analyzing || files.length === 0} data-testid="analyzer-submit">
            {analyzing ? (
              <>
                <Sparkles className="mr-2 h-4 w-4 animate-pulse" />
                {scoring
                  ? "Gemini is scoring the policy…"
                  : progress < 100
                  ? `Uploading ${progress}%`
                  : "Preparing the analysis…"}
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" /> Analyze {files.length > 1 ? `${files.length} PDFs` : "policy"}
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </form>
  );
}

/* -------------------------------- Result view -------------------------------- */

function AnalysisView({ analysis, onDelete, onReanalyze }) {
  const verdict = VERDICT_STYLES[analysis.verdict] || VERDICT_STYLES.average;
  return (
    <div className="mt-8 flex flex-col gap-6" data-testid="analyzer-result">
      <Card className={`overflow-hidden border ${verdict.ring}`}>
        <CardContent className="grid gap-6 p-6 md:grid-cols-[auto_1fr]">
          <ScoreDial score={analysis.overall_score} verdict={verdict} />
          <div className="flex flex-col gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted">
                {analysis.insurer || "Insurer not detected"}
              </div>
              <h2 className="font-serif text-2xl text-text">
                {analysis.product_name || analysis.filename}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest ${verdict.chip}`}>
                {verdict.label}
              </span>
              <span className="rounded-full bg-elevated px-3 py-1 text-[10px] uppercase tracking-widest text-muted">
                Confidence: {analysis.confidence}
              </span>
              <span className="rounded-full bg-elevated px-3 py-1 text-[10px] uppercase tracking-widest text-muted">
                {analysis.page_count} {analysis.page_count === 1 ? "page" : "pages"}
              </span>
              <span className="rounded-full bg-elevated px-3 py-1 text-[10px] uppercase tracking-widest text-muted">
                {formatDate(analysis.created_at)}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {(analysis.attachments && analysis.attachments.length > 0
                ? analysis.attachments
                : analysis.has_pdf
                ? [{ index: 0, filename: analysis.filename, has_pdf: true }]
                : []
              ).map((att) => (
                <a
                  key={`att-${att.index}`}
                  href={analysisPdfUrl(analysis.id, att.index)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1.5 rounded-md border border-border bg-elevated px-3 py-1.5 text-xs text-text hover:border-gold hover:text-gold ${
                    att.has_pdf ? "" : "pointer-events-none opacity-50"
                  }`}
                  data-testid={`analyzer-download-pdf-${att.index}`}
                >
                  <Download className="h-3.5 w-3.5" />
                  <span className="max-w-[220px] truncate">
                    {att.filename || (att.index === 0 ? "Primary policy" : `Doc ${att.index + 1}`)}
                  </span>
                </a>
              ))}
              <button
                type="button"
                onClick={onReanalyze}
                disabled={!onReanalyze || !analysis.can_reanalyze}
                className="inline-flex items-center gap-1.5 rounded-md border border-gold/40 bg-gold/5 px-3 py-1.5 text-xs font-medium text-gold hover:border-gold hover:bg-gold/10 disabled:pointer-events-none disabled:opacity-40"
                title={
                  analysis.can_reanalyze
                    ? "Re-run Gemini on this policy with the latest scoring rubric"
                    : "This analysis pre-dates re-analyze support — please upload the PDF again."
                }
                data-testid="analyzer-reanalyze"
              >
                <Sparkles className="h-3.5 w-3.5" /> Re-analyze
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs text-muted hover:border-red-500 hover:text-red-400"
                data-testid="analyzer-delete"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete analysis
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {analysis.insurer_kpis && <InsurerKpiCard kpis={analysis.insurer_kpis} />}

      <SectionScorecard analysis={analysis} />

      {analysis.recommendations?.length > 0 && (
        <Card>
          <CardContent className="flex flex-col gap-3 p-6">
            <h3 className="font-serif text-xl text-text">What to do about it</h3>
            <ul className="flex flex-col gap-2">
              {analysis.recommendations.map((r, i) => (
                <li
                  key={`rec-${i}-${r.slice(0, 24)}`}
                  className="flex items-start gap-2 rounded-md border border-gold/15 bg-gold/5 px-3 py-2 text-sm text-text"
                >
                  <ChevronRight className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gold" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <p className="text-center text-xs text-muted">
        Educational only — not investment advice or a substitute for the actual policy wording.
      </p>
    </div>
  );
}

function ScoreDial({ score, verdict }) {
  const clamped = Math.max(0, Math.min(100, score || 0));
  // SVG circular progress
  const size = 128;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (clamped / 100) * c;
  return (
    <div className="flex flex-shrink-0 items-center justify-center">
      <div className="relative">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} className="fill-none stroke-border" />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            strokeWidth={stroke}
            className={`fill-none transition-all duration-1000 ${verdict.tone}`}
            style={{ stroke: "currentColor", strokeDasharray: `${dash} ${c}`, strokeLinecap: "round" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-serif text-4xl ${verdict.tone}`}>{clamped}</span>
          <span className="text-[10px] uppercase tracking-widest text-muted">/ 100</span>
        </div>
      </div>
    </div>
  );
}

function CriterionRow({ criterion }) {
  // `applicable === false` means Gemini couldn't find this clause in the PDF — render it
  // in a muted "unknown, please verify manually" style and don't show a numeric score.
  const notMentioned = criterion.applicable === false;
  if (notMentioned) {
    const chip = IMPORTANCE_CHIP[criterion.importance] || IMPORTANCE_CHIP.important;
    return (
      <li
        className="flex items-start gap-3 py-3 first:pt-0 last:pb-0 opacity-70"
        data-testid={`criterion-${criterion.key}`}
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-300" />
        <div className="flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium text-text">{criterion.label}</span>
            <div className="flex items-center gap-2">
              {criterion.importance && (
                <span className={`rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-widest ${chip}`}>
                  {criterion.importance.replace(/_/g, " ")}
                </span>
              )}
              <span
                className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-amber-300"
                data-testid={`criterion-excluded-${criterion.key}`}
              >
                Excluded from score
              </span>
            </div>
          </div>
          <p className="mt-1 text-xs text-amber-300/90">
            Not mentioned in policy — verify this clause manually before you buy.
          </p>
          {criterion.guide && <p className="mt-1 text-xs text-muted">{criterion.guide}</p>}
        </div>
      </li>
    );
  }

  const met = criterion.met;
  const Icon = met ? CheckCircle2 : criterion.score >= 3 ? AlertTriangle : XCircle;
  const iconTone = met ? "text-emerald-400" : criterion.score >= 3 ? "text-amber-300" : "text-red-400";
  const chip = IMPORTANCE_CHIP[criterion.importance] || IMPORTANCE_CHIP.important;
  return (
    <li className="flex items-start gap-3 py-3 first:pt-0 last:pb-0" data-testid={`criterion-${criterion.key}`}>
      <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${iconTone}`} />
      <div className="flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-medium text-text">{criterion.label}</span>
          <div className="flex items-center gap-2">
            {criterion.importance && (
              <span className={`rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-widest ${chip}`}>
                {criterion.importance.replace(/_/g, " ")}
              </span>
            )}
            <span className="rounded-full bg-elevated px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted">
              {criterion.score} / 10
            </span>
          </div>
        </div>
        {criterion.evidence && (
          <p className="mt-1 text-xs italic text-muted">&ldquo;{criterion.evidence}&rdquo;</p>
        )}
        {criterion.note && <p className="mt-1 text-xs text-text/80">{criterion.note}</p>}
        {criterion.pro_tip && (
          <p className="mt-1 text-[11px] text-gold/80">
            <span className="font-semibold">Pro tip: </span>
            {criterion.pro_tip}
          </p>
        )}
      </div>
    </li>
  );
}

/* ------------------------------ Criteria preview ------------------------------ */

function CriteriaPreview({ criteria }) {
  if (!criteria.length) return null;
  return (
    <div className="mt-10">
      <h3 className="font-serif text-lg text-text">What we&apos;ll check</h3>
      <p className="mt-1 text-xs text-muted">
        {criteria.length} must-have features grouped into Waiting Periods, Coverage, Financial
        Terms and Claim Experience — plus the insurer&apos;s IRDAI-published KPIs.
      </p>
      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {criteria.map((c) => (
          <div
            key={c.key}
            className="rounded-md border border-border bg-card/60 px-3 py-2 text-xs"
            data-testid={`criteria-preview-${c.key}`}
          >
            <div className="font-medium text-text">{c.label}</div>
            <div className="mt-0.5 text-muted">{c.guide}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Section scorecard + Insurer-KPI card ---------- */

const SECTION_META = {
  waiting_periods: { label: "Waiting Periods", blurb: "Lock-in windows before your cover kicks in." },
  coverage: { label: "Coverage Benefits", blurb: "What the policy actually pays for." },
  financial: { label: "Financial Terms", blurb: "Sum insured, deductibles, sub-limits, premium behaviour." },
  claims: { label: "Claim Experience", blurb: "TATs, TPA vs in-house, portability rights." },
};

const IMPORTANCE_CHIP = {
  critical: "bg-red-500/10 text-red-300 border-red-400/30",
  important: "bg-amber-500/10 text-amber-300 border-amber-400/30",
  good_to_have: "bg-emerald-500/10 text-emerald-300 border-emerald-400/30",
};

function scoreTone(score) {
  if (score >= 80) return "text-emerald-300";
  if (score >= 60) return "text-amber-300";
  return "text-red-400";
}

function SectionScorecard({ analysis }) {
  const [openSection, setOpenSection] = useState(null);
  const criteria = analysis.criteria || [];
  const sections = ["waiting_periods", "coverage", "financial", "claims"];
  const excludedCriteria = criteria.filter((c) => c.applicable === false);
  const scoredCriteria = criteria.filter((c) => c.applicable !== false);
  return (
    <>
    {excludedCriteria.length > 0 && (
      <div
        className="rounded-lg border border-amber-400/40 bg-amber-400/5 p-4 text-sm text-amber-200"
        data-testid="analyzer-transparency-banner"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <div className="font-medium">
              {excludedCriteria.length} of {criteria.length} features weren&apos;t mentioned in the PDF
            </div>
            <div className="mt-0.5 text-xs text-amber-200/80">
              To keep the score honest, we excluded those from your <span className="font-semibold">{analysis.overall_score}/100</span> —
              it&apos;s calculated over the {scoredCriteria.length} features Gemini could evaluate. Review the
              highlighted items below and verify them manually before you buy.
            </div>
          </div>
        </div>
      </div>
    )}
    <Card data-testid="analyzer-sections">
      <CardContent className="flex flex-col gap-3 p-6">
        <div>
          <h3 className="font-serif text-xl text-text">Scorecard by section</h3>
          <p className="text-xs text-muted">
            {scoredCriteria.length} scored · {excludedCriteria.length} not mentioned · grouped into 4 sections. Tap a section to expand.
          </p>
        </div>
        <div className="mt-2 flex flex-col divide-y divide-border">
          {sections.map((secKey) => {
            const meta = SECTION_META[secKey];
            const secScore = analysis.section_scores?.[secKey];
            const items = criteria.filter((c) => c.section === secKey);
            const secScored = items.filter((c) => c.applicable !== false);
            const secExcluded = items.filter((c) => c.applicable === false);
            const isOpen = openSection === secKey;
            return (
              <div key={secKey} data-testid={`analyzer-section-${secKey}`}>
                <button
                  type="button"
                  onClick={() => setOpenSection(isOpen ? null : secKey)}
                  className="flex w-full items-center justify-between gap-3 py-3 text-left hover:bg-elevated/30"
                  data-testid={`analyzer-section-toggle-${secKey}`}
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-text">{meta.label}</div>
                    <div className="text-[11px] text-muted">
                      {meta.blurb} · {secScored.length} scored
                      {secExcluded.length > 0 && `, ${secExcluded.length} not mentioned`}
                    </div>
                  </div>
                  <div className={`text-lg font-serif ${secScore == null ? "text-muted" : scoreTone(secScore)}`}>
                    {secScore == null ? "—" : secScore}
                  </div>
                  <ChevronRight
                    className={`h-4 w-4 text-muted transition-transform ${isOpen ? "rotate-90" : ""}`}
                  />
                </button>
                {isOpen && (
                  <ul className="ml-2 mt-1 mb-3 flex flex-col divide-y divide-border">
                    {items.map((c) => (
                      <CriterionRow key={c.key} criterion={c} />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
    {excludedCriteria.length > 0 && (
      <Card data-testid="analyzer-excluded-list">
        <CardContent className="flex flex-col gap-3 p-6">
          <div>
            <h3 className="font-serif text-xl text-text">Not mentioned in policy — excluded from score</h3>
            <p className="mt-1 text-xs text-muted">
              These clauses weren&apos;t found in the PDF text. That doesn&apos;t automatically mean they&apos;re
              excluded from the policy — sometimes the wording lives in a separate T&amp;C document. Please
              verify each one manually with the insurer before buying.
            </p>
          </div>
          <ul className="flex flex-col divide-y divide-border">
            {excludedCriteria.map((c) => (
              <CriterionRow key={c.key} criterion={c} />
            ))}
          </ul>
        </CardContent>
      </Card>
    )}
    </>
  );
}

function kpiVerdict(field, value) {
  // Simple thresholds — mirror the founder's guidance in the criteria list.
  if (value == null) return { tone: "muted", chip: "border-border bg-elevated text-muted" };
  if (field === "csr") {
    if (value >= 95) return { tone: "excellent", chip: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" };
    if (value >= 90) return { tone: "good", chip: "border-emerald-400/30 bg-emerald-400/5 text-emerald-300" };
    if (value >= 85) return { tone: "watch", chip: "border-amber-400/40 bg-amber-400/10 text-amber-300" };
    return { tone: "poor", chip: "border-red-400/40 bg-red-400/10 text-red-300" };
  }
  if (field === "icr") {
    if (value >= 60 && value <= 85) return { tone: "sweet", chip: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" };
    if (value < 60) return { tone: "over-reject", chip: "border-amber-400/40 bg-amber-400/10 text-amber-300" };
    if (value > 90) return { tone: "stress", chip: "border-red-400/40 bg-red-400/10 text-red-300" };
    return { tone: "ok", chip: "border-border bg-elevated text-text" };
  }
  if (field === "solvency_ratio") {
    if (value >= 1.8) return { tone: "healthy", chip: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" };
    if (value >= 1.5) return { tone: "compliant", chip: "border-amber-400/40 bg-amber-400/10 text-amber-300" };
    return { tone: "breach", chip: "border-red-400/40 bg-red-400/10 text-red-300" };
  }
  return { tone: "muted", chip: "border-border bg-elevated text-text" };
}

function InsurerKpiCard({ kpis }) {
  const rows = [
    {
      field: "csr", label: "Claim Settlement Ratio", suffix: "%",
      tip: "% of claims paid vs received. Higher is better.",
      bands: [
        { label: "Excellent", range: ">95%" },
        { label: "Good", range: "90–95%" },
        { label: "Needs review", range: "<90%" },
      ],
    },
    {
      field: "icr", label: "Incurred Claim Ratio", suffix: "%",
      tip: "Claims paid vs premium collected. 60–85% is the healthy band.",
      bands: [
        { label: "Sweet spot", range: "60–85%" },
        { label: "Watch — over-rejecting", range: "<60%" },
        { label: "Watch — financial stress", range: ">90%" },
      ],
    },
    {
      field: "solvency_ratio", label: "Solvency Ratio", suffix: "",
      tip: "Insurer's cushion above liabilities. IRDAI mandates ≥ 1.50.",
      bands: [
        { label: "Healthy", range: ">1.80" },
        { label: "Compliant", range: "1.50–1.80" },
        { label: "Breach (IRDAI limit)", range: "<1.50" },
      ],
    },
    {
      field: "complaint_ratio", label: "Complaint Ratio", suffix: " /10K claims",
      tip: "Fewer complaints per 10,000 claims = better service.",
      bands: [
        { label: "Excellent", range: "<5" },
        { label: "Good", range: "5–15" },
        { label: "Needs review", range: ">15" },
      ],
    },
    {
      field: "network_hospitals", label: "Network Hospitals", suffix: "",
      tip: "Cashless-empanelled hospitals nationally.",
      bands: [
        { label: "Excellent", range: ">10,000" },
        { label: "Good", range: "5,000–10,000" },
        { label: "Needs review", range: "<5,000" },
      ],
    },
  ];
  return (
    <Card data-testid="analyzer-insurer-kpis" className="border-gold/30">
      <CardContent className="flex flex-col gap-3 p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-serif text-xl text-text">Insurer KPIs (IRDAI)</h3>
            <p className="text-xs text-muted">
              Sourced from {kpis.source || "IRDAI Annual Report"} · Fiscal year {kpis.fiscal_year || "—"}
              {kpis.last_updated && ` · Updated ${formatDate(kpis.last_updated)}`}
            </p>
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => {
            const v = kpis[r.field];
            const v_ = v == null ? "—" : `${typeof v === "number" && r.suffix === "%" ? v.toFixed(2) : v}${r.suffix}`;
            const meta = kpiVerdict(r.field, v);
            return (
              <div
                key={r.field}
                className={`flex flex-col gap-1.5 rounded-md border p-3 text-xs ${meta.chip}`}
                data-testid={`kpi-${r.field}`}
              >
                <div className="text-[10px] uppercase tracking-widest opacity-80">{r.label}</div>
                <div className="font-serif text-2xl">{v_}</div>
                <div className="text-[11px] opacity-70">{r.tip}</div>
                <div className="mt-1 flex flex-col gap-0.5 border-t border-white/10 pt-1.5">
                  {r.bands.map((b) => (
                    <div key={b.label} className="flex justify-between text-[10px] opacity-80">
                      <span>{b.label}</span>
                      <span className="font-mono">{b.range}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


/* ------------------------------ History section ------------------------------ */

function HistorySection({ history, onOpen, onDelete }) {
  if (!history.length) return null;
  return (
    <div className="mt-12">
      <h3 className="font-serif text-xl text-text">Past analyses</h3>
      <p className="mt-1 text-xs text-muted">{history.length} on file</p>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {history.map((h) => {
          const v = VERDICT_STYLES[h.verdict] || VERDICT_STYLES.average;
          return (
            <div
              key={h.id}
              className="group flex items-start justify-between gap-3 rounded-lg border border-border bg-card p-4 transition-colors hover:border-gold/40"
              data-testid={`history-item-${h.id}`}
            >
              <button
                type="button"
                onClick={async () => {
                  try {
                    const detail = await import("../lib/api").then((m) => m.fetchAnalysis(h.id));
                    onOpen(detail);
                  } catch {
                    toast.error("Couldn't load that analysis.");
                  }
                }}
                className="flex-1 text-left"
              >
                <div className="flex items-center gap-2">
                  <span className={`inline-flex h-8 w-8 items-center justify-center rounded-md font-serif text-sm ${v.chip}`}>
                    {h.overall_score}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-sm text-text">{h.product_name || h.filename}</div>
                    <div className="truncate text-xs text-muted">
                      {h.insurer || "—"} · {formatDate(h.created_at)}
                    </div>
                  </div>
                </div>
              </button>
              <div className="flex flex-col gap-1">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${v.chip}`}>
                  {v.label}
                </span>
                <button
                  type="button"
                  onClick={() => onDelete(h.id)}
                  className="text-[10px] text-muted hover:text-red-400"
                >
                  <Trash2 className="mx-auto h-3 w-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AnalyzePolicyPage;
